'use client';

import { useEffect, useState } from 'react';

import { Calendar, LocationFilled } from '@carbon/icons-react';
import { Column, Grid, PaginationNav, Tab, TabList, Tabs } from '@carbon/react';

import type { ContentCardProps } from '@/components/ContentCard';
import { ContentCard } from '@/components/ContentCard';
import { useWindowSize } from '@/hooks/useWindowSize';
import type { FallFestQuantumEventWithImage } from '@/types/FallFestEvents';
import type { TagType } from '@/types/TagType';

import { FallFestSearch } from '@/app/events/fall-fest-2026/FallFestSearch';

interface Props {
  upcomingEvents: FallFestQuantumEventWithImage[];
  pastEvents: FallFestQuantumEventWithImage[];
}

const PAGES_TO_SHOW = 4;
const breakpointsPx = {
  lg: 1056,
  md: 672,
};

export function FallFestEventsGrid({ upcomingEvents, pastEvents }: Props) {
  const windowSize = useWindowSize();
  const initEventsPerPage = getEventsPerPage(windowSize.width);
  const [eventsPerPage, setEventsPerPage] = useState(initEventsPerPage);
  const [selectedTab, setSelectedTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [tabs, setTabs] = useState<string[]>([]);
  const [search, setSearch] = useState<string | null>(null);

  useEffect(() => {
    const upcomingCount = filterEventsBySearch(upcomingEvents, search).length;
    const pastCount = filterEventsBySearch(pastEvents, search).length;
    setTabs([`Upcoming events (${upcomingCount})`, `Past events (${pastCount})`]);
    setCurrentPage(0);
  }, [upcomingEvents, pastEvents, search]);

  function handleTabChange({ selectedIndex }: { selectedIndex: number }) {
    setSelectedTab(selectedIndex);
    setCurrentPage(0);
  }

  const tabEvents = selectedTab === 0 ? upcomingEvents : pastEvents;
  const searchFilteredEvents = filterEventsBySearch(tabEvents, search);

  const currentPageStart = currentPage * eventsPerPage;
  const currentPageEnd = currentPage * eventsPerPage + eventsPerPage;
  const paginatedEvents = searchFilteredEvents.slice(currentPageStart, currentPageEnd);

  const totalPages = Math.ceil(searchFilteredEvents.length / eventsPerPage);

  function onPageChange(pageIndex: number) {
    setCurrentPage(pageIndex);

    if (document) {
      const eventTabs = document.getElementById('quantum-events-tabs');
      if (eventTabs) {
        eventTabs.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  useEffect(() => {
    const newEventsPerPage = getEventsPerPage(windowSize.width);
    setEventsPerPage(newEventsPerPage);
  }, [windowSize]);

  return (
    <>
      <FallFestSearch setSearch={setSearch} />
      <Grid className="scroll-mt-[112px]" id="quantum-events-tabs">
        <Column sm="100%">
          <Tabs onChange={handleTabChange}>
            <TabList
              aria-label={'Events tab filter'}
              className="mb-24 border-b border-border-subtle-00"
            >
              {tabs.map((title) => (
                <Tab key={title} className="text-text-helper">
                  {title}
                </Tab>
              ))}
            </TabList>
          </Tabs>
        </Column>
      </Grid>
      {searchFilteredEvents.length === 0 && (
        <Grid data-testid="events-grid">
          <Column sm={4} md={8} lg={16}>
            <div className="mb-32">
              {selectedTab === 0 ? (
                <p className="text-h5">No upcoming events found.</p>
              ) : (
                <p className="text-h5">No past events found.</p>
              )}
            </div>
          </Column>
        </Grid>
      )}
      <Grid
        className="gap-y-48 p-16 [--cds-grid-columns:1] md:p-0 md:[--cds-grid-columns:2] lg:[--cds-grid-columns:3]"
        data-testid="events-grid"
      >
        {paginatedEvents.map((event) => {
          const image: ContentCardProps['image'] = {
            src: event.image,
            alt: '',
            desaturate: selectedTab === 1,
            loading: 'eager',
          };

          const date = fallFestMonthYear(event.startDate);

          const tags: { text: string; type: TagType }[] = [];
          for (const tag of event.tags) {
            // if 'In person & virtual, do not push the individual 'In person' or 'Virtual' tags to the card
            if (event.tags.includes('In person & virtual')) {
              if (tag === 'In person' || tag === 'Virtual') {
                continue;
              }
            }
            tags.push({ text: tag, type: 'outline' as const });
          }

          return (
            <Column key={event.id} className="relative">
              <ContentCard
                image={image}
                title={event.name}
                link={{
                  label: 'View details',
                  url: event.link,
                }}
                tags={tags}
                labelsWithIcon={[
                  { label: date, icon: <Calendar /> },
                  {
                    label: event.location,
                    icon: <LocationFilled />,
                  },
                ]}
              />
            </Column>
          );
        })}
      </Grid>
      {totalPages > 1 && (
        <Grid className="mt-32 flex justify-end">
          <Column sm="100%">
            <PaginationNav
              itemsShown={PAGES_TO_SHOW}
              page={currentPage}
              totalItems={totalPages}
              onChange={onPageChange}
            />
          </Column>
        </Grid>
      )}
    </>
  );
}

function getEventsPerPage(windowSize: number | undefined) {
  if (!windowSize) {
    return 9;
  }

  if (windowSize >= breakpointsPx.lg) {
    return 12;
  } else if (windowSize < breakpointsPx.lg && windowSize > breakpointsPx.md) {
    return 14;
  } else {
    return 6;
  }
}

function filterEventsBySearch(
  events: FallFestQuantumEventWithImage[],
  search: string | null
): FallFestQuantumEventWithImage[] {
  if (!search) {
    return events;
  }

  return events.filter((event) => filterEventBySearch(event, search));
}

function filterEventBySearch(event: FallFestQuantumEventWithImage, search: string): boolean {
  const lowerSearch = search.toLocaleLowerCase();

  return (
    event.name.toLocaleLowerCase().includes(lowerSearch) ||
    (event.country?.toLocaleLowerCase() || '').includes(lowerSearch) ||
    (event.location?.toLocaleLowerCase() || '').includes(lowerSearch)
  );
}

// Airtable doesn't store each event's timezone, so we use Eastern after agreed with events team.
const FALL_FEST_EVENT_TIMEZONE = 'America/New_York';

function fallFestMonthYear(date: Date | undefined) {
  if (!date) {
    return '';
  }

  return new Intl.DateTimeFormat('en', {
    dateStyle: 'long',
    timeZone: FALL_FEST_EVENT_TIMEZONE,
  }).format(date);
}
